<template>
  <v-row>
    <v-row>
      <v-col class="align-left">
        <ExpandCollapseToggle
          id="maintenancePanelToggle"
          @toggleAllPanels="toggleAllPanels"
          styleClass="mt-3"
          :panel="panel"
        />
      </v-col>
      <v-col class="align-right mt-2">
        <ValidationCountsHeader v-if="moduleType == MODULE_TYPES.MANAGEMENT" />
      </v-col>
    </v-row>
    <v-col sm="12" md="12" lg="12" xl="12" class="pa-0">
      <v-expansion-panels v-model="panel" multiple color="#fff">
        <v-expansion-panel class="mt-3">
          <v-expansion-panel-title
            class="d-flex justify-start"
            hide-actions
            expand-icon="fa-solid fa-plus"
            collapse-icon="fa-solid fa-minus"
          >
            <v-icon v-if="panel?.includes(0)" icon="fa-solid fa-minus" />

            <v-icon v-else icon="fa-solid fa-plus" />

            <h3>Proposed Candidates</h3>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <MaintenanceTable
              ref="proposedMaintenanceTable"
              :items="proposed"
              :isEditable="isEditable"
              canAddOrSubmit
              @save="(...args) => saveCandidate(...args)"
              @show-message="(...args) => showMessageEvent(...args, 'proposed')"
              @hide-message="hideMessageEvent('proposed')"
              @show-change-history-dialog="(payload) => changePriorityHistory(payload)"
              @submit-Maintenance-Items-To-SAP="submitMaintenanceItemsToSAP()"
              id="proposed"
              :brkey="maintenanceValue?.brkey"
              :servTypOn="structureServTypOn"
              :structureUnits="maintenanceValue?.structureUnits"
              :moduleType="moduleType"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-title
            class="d-flex justify-start"
            hide-actions
            expand-icon="fa-solid fa-plus"
            collapse-icon="fa-solid fa-minus"
          >
            <v-icon v-if="panel?.includes(1)" icon="fa-solid fa-minus" />

            <v-icon v-else icon="fa-solid fa-plus" />

            <h3>Completed Candidates</h3>
          </v-expansion-panel-title>

          <v-expansion-panel-text>
            <MaintenanceTable
              ref="completedMaintenanceTable"
              :items="completed"
              :isEditable="isEditable"
              isCompletedCandidates
              @save="(...args) => saveCandidate(...args)"
              @show-message="(...args) => showMessageEvent(...args, 'completed')"
              @hide-message="hideMessageEvent('completed')"
              @show-change-history-dialog="(payload) => changePriorityHistory(payload)"
              id="completed"
              :brkey="maintenanceValue?.brkey"
              :servTypOn="structureServTypOn"
              :structureUnits="maintenanceValue?.structureUnits"
              :moduleType="moduleType"
            />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-dialog v-model="changeHistoryDialog" :width="800">
        <v-card>
          <v-card-title>
            <span class="text-h5">M.05 Priority Change History</span>
          </v-card-title>
          <v-card-text>
            <v-table class="priorityChangeHistoryTable">
              <thead>
                <tr>
                  <th id="priority" class="text-left">Priority</th>
                  <th id="data" class="text-left">Set Date</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(child, childIndex) in getPriorityHistory(selectedWcId)" :key="childIndex">
                  <td>{{ configStore.getReferenceValue(REFERENCE_TABLE.AGENCY_PRIORITY, child.agencyPriority) }}</td>
                  <td>{{ new Date(child.updateDateTime).toLocaleString() }}</td>
                </tr>
              </tbody>
            </v-table>
            <button
              @click="changeHistoryDialog = false"
              class="d-flex align-end flex-column align-content-center close-button"
            >
              <v-icon class="no-outline" size="x-large" icon="fas fa-xmark mx-2" />
              <small>CLOSE</small>
            </button>
          </v-card-text>
        </v-card>
      </v-dialog>
    </v-col>
  </v-row>
</template>

<script>
import { defineComponent, ref, computed, watch, onMounted, nextTick } from "vue";
import { useInspectionStore } from "@/stores/inspection";
import { clone } from "@/util/clone";
import MaintenanceTable from "@/components/shared/MaintenanceTable.vue";
import { MAINTENANCE_CANDIDATE_TYPE, MODULE_TYPES, INSPECTION_MESSAGES } from "@/constants/InspectionConstants";
import ExpandCollapseToggle from "@/components/shared/ExpandCollapseToggle.vue";
import { REFERENCE_TABLE } from "@/constants/ReferenceTables";
import { useSnackbarStore } from "@/stores/snackbar";
import { useMaintenanceStore } from "@/components/inspection/store/maintenance";
import { useQuickSearchStore } from "@/stores/quickSearch";
import { INVENTORY_LINK_MAINTENANCE_PERMISSIONS } from "@/rbac";
import { ACCESS_LEVELS } from "@/rbac/accessLevels.js";
import { useUserPermissionStore } from "@/stores/userPermission";
import { useInventoryStore } from "@/stores/inventory";
import { SNACKBAR_MESSAGE_TYPES, SNACKBAR_MARKER } from "@/constants/GlobalSnackbar.js";
import { useConfigStore } from "@/stores/config";
import ValidationCountsHeader from "@/components/shared/ValidationCountsHeader.vue";

export default defineComponent({
  name: "InspectionMaintenance",
  props: {
    moduleType: { default: MODULE_TYPES.INSPECTION },
  },
  setup(props) {
    const inspectionStore = useInspectionStore();
    const snackbarStore = useSnackbarStore();
    const maintenanceStore = useMaintenanceStore();
    const quickSearchStore = useQuickSearchStore();
    const userPermissionStore = useUserPermissionStore();
    const structureHeader = computed(() => quickSearchStore.getStructure());
    const structure = computed(() => inspectionStore.selectedInspection);
    const allPanels = [0, 1];
    const changeHistoryDialog = ref(false);
    const activeSource = ref(null);
    const selectedWcId = ref(null);
    const configStore = useConfigStore();
    let panel = ref([0]);
    let snackBarId = ref("");
    let proposedSnackbarId = ref("");
    let submitToSAPSnackbarIdFailure = ref([]);
    let submitToSAPSnackbarIdSuccess = ref(null);
    let completedSnackbarId = ref("");
    let maintenanceValue = ref({});
    let completed = ref([]);
    let proposed = ref([]);
    let completedMaintenanceTable = ref(null);
    let proposedMaintenanceTable = ref(null);
    const inventoryStore = useInventoryStore();
    let isDirty = computed({
      get() {
        if (props.moduleType === MODULE_TYPES.INSPECTION) {
          inspectionStore.getDirtyFlag();
          return;
        }
        return inventoryStore.getIsDirty();
      },
      set(value) {
        if (props.moduleType === MODULE_TYPES.INSPECTION) {
          inspectionStore.setDirtyFlag(value);
          return;
        }
        inventoryStore.setIsDirty(value);
      },
    });
    const hasAccessToEdit = computed({
      get() {
        return userPermissionStore.checkUserPermission(
          INVENTORY_LINK_MAINTENANCE_PERMISSIONS[ACCESS_LEVELS.EDIT],
          userPermissionStore.getUserPermissionByBrkeyFromCache(maintenanceValue.value.brkey)
        );
      },
    });

    const structureServTypOn = computed({
      get() {
        if (props.moduleType === MODULE_TYPES.INSPECTION) {
          return structure.value?.structureBridge?.servTypOn;
        }
        return structureHeader.value?.SERVTYPON;
      },
    });

    const changePriorityHistory = (payload) => {
      activeSource.value = payload.id;
      selectedWcId.value = payload.wcId;
      changeHistoryDialog.value = true;
    };

    const isEditable = computed(() => {
      if (props.moduleType === MODULE_TYPES.INSPECTION) {
        return inspectionStore.isEditable;
      } else if (props.moduleType === MODULE_TYPES.MANAGEMENT) {
        return !quickSearchStore.getErrorMessage() && maintenanceValue.value?.brkey && hasAccessToEdit.value;
      } else {
        return false;
      }
    });
    const getCandidates = (candidateType) => {
      let cloneWCand = clone(maintenanceValue.value?.inspectionWcands || []);
      let statuses = getStatuses(candidateType);

      for (const [index, c] of cloneWCand.entries()) {
        if (statuses?.includes(c?.agencyStatus)) {
          c.readOnly = true;
          c.isAdd = false;
          c.uniqueKey = index;
          if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
            proposed.value.push(c);
          } else {
            completed.value.push(c);
          }
        }
      }
      if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
        sortCandidates(proposed);
      }
    };
    //sort candidates on load
    const sortCandidates = (proposed) => {
      proposed.value.sort(function (a, b) {
        const agencyPriorityComparison = a.agencyPriority.localeCompare(b.agencyPriority);
        if (agencyPriorityComparison === 0) {
          const dateA = a.workRecDate ? new Date(a.workRecDate) : null;
          const dateB = b.workRecDate ? new Date(b.workRecDate) : null;
          if (dateA === null && dateB === null) {
            return 0;
          } else if (dateA === null) {
            return 1;
          } else if (dateB === null) {
            return -1;
          }
          return dateA - dateB;
        }
      });
    };

    const getStatuses = (candidateType) => {
      if (candidateType == MAINTENANCE_CANDIDATE_TYPE.PROPOSED) {
        proposed.value = [];
        return ["0", "1", "2", "3", "4", "D"];
      } else {
        completed.value = [];
        return ["5", "6", "7"];
      }
    };

    const resetItems = () => {
      nextTick(() => {
        completedMaintenanceTable?.value?.resetItems();
        proposedMaintenanceTable?.value?.resetItems();
      });
    };

    const loadCandidates = () => {
      getCandidates(MAINTENANCE_CANDIDATE_TYPE.PROPOSED);
      getCandidates(MAINTENANCE_CANDIDATE_TYPE.COMPLETED);
      resetItems();
    };

    const saveCandidate = (detail) => {
      detail.strUnitKey = detail.strUnitKey ? detail.strUnitKey : -1;
      if (detail.isAdd) {
        maintenanceValue.value.inspectionWcands.push(detail);
      } else {
        //update existing
        const updateIndex = maintenanceValue.value.inspectionWcands.findIndex((a) => a?.wcId == detail?.wcId);
        maintenanceValue.value.inspectionWcands.splice(updateIndex, 1, detail);
      }
      loadCandidates();
      isDirty.value = true;
    };

    const showMessageEvent = (message, candidate) => {
      if (candidate == "completed") {
        completedSnackbarId.value = snackbarStore.showMessage({
          displayText: message,
        });
      } else {
        proposedSnackbarId.value = snackbarStore.showMessage({
          displayText: message,
        });
      }
    };

    const hideMessageEvent = (candidate) => {
      if (candidate == "completed") {
        snackbarStore.removeMessage(completedSnackbarId.value);
      } else {
        snackbarStore.removeMessage(proposedSnackbarId.value);
      }
    };

    const toggleAllPanels = (expandAll) => {
      if (expandAll) {
        panel.value = allPanels;
      } else {
        panel.value = [];
      }
    };

    const readDataFromAPI = async () => {
      maintenanceValue.value = {};
      isDirty.value = false;
      snackbarStore.removeMessage(completedSnackbarId.value);
      snackbarStore.removeMessage(proposedSnackbarId.value);
      clearSubmitToSAPSnackBars();
      if (!quickSearchStore.getErrorMessage() && structureHeader.value?.BRKEY) {
        await maintenanceStore.loadMaintenanceObject(props.moduleType, structureHeader.value.BRKEY, null);
      }
      inventoryStore.isAddingOrEditingMaintenanceTable = false;
      maintenanceValue.value = maintenanceStore.getMaintenanceObject();
      loadCandidates();
      isDirty.value = false;
    };

    const readData = async () => {
      maintenanceValue.value = {};
      isDirty.value = false;
      snackbarStore.removeMessage(completedSnackbarId.value);
      snackbarStore.removeMessage(proposedSnackbarId.value);
      clearSubmitToSAPSnackBars();
      await maintenanceStore.loadMaintenanceObject(props.moduleType, null, structure.value);
      maintenanceValue.value = maintenanceStore.getMaintenanceObject();
      loadCandidates();
      isDirty.value = false;
    };

    const getPriorityHistory = (wcId) => {
      const parent = maintenanceValue.value?.inspectionWcands
        ? maintenanceValue.value?.inspectionWcands.find((p) => p.wcId === wcId)
        : { inspectionWCANDAgencyPriorityHists: [] };

      return (
        parent?.inspectionWCANDAgencyPriorityHists?.sort(
          (a, b) => new Date(b.updateDateTime) - new Date(a.updateDateTime)
        ) || []
      );
    };

    const saveChanges = async () => {
      inspectionStore.setShowValidationSnackbar(false);
      const isSuccess = await maintenanceStore.saveMaintenance(
        props.moduleType,
        maintenanceValue.value,
        structure.value
      );
      if (isSuccess) {
        if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
          await readDataFromAPI();
        } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
          await readData();
        }
      }
      return isSuccess;
    };

    const submitMaintenanceItemsToSAP = async () => {
      clearSubmitToSAPSnackBars();
      submitToSAPSnackbarIdFailure.value = [];
      inventoryStore.setIsSearching(true);
      const isSuccess = await maintenanceStore.submitMaintenanceItemsToSAP(maintenanceValue.value.brkey);
      if (isSuccess) {
        if (maintenanceStore.submitToSAPErrorMessages.length > 0) {
          for (const [index, msg] of maintenanceStore.submitToSAPErrorMessages.entries()) {
            submitToSAPSnackbarIdFailure.value.push(
              snackbarStore.showMessage({
                displayText: msg,
                timeout: 30000,
                messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
                marker: SNACKBAR_MARKER.MAINTENANCE,
                callback: () => {
                  snackbarStore.removeMessage(submitToSAPSnackbarIdFailure.value[index]);
                }, // Reset the validationSnackbar to hide the message
              })
            );
          }
        }
        if (maintenanceStore.candidatesSubmittedToSAP > 0) {
          await readDataFromAPI();
          submitToSAPSnackbarIdSuccess.value = snackbarStore.showMessage({
            displayText: INSPECTION_MESSAGES.WORK_ITEMS_SENT_TO_SAP + maintenanceStore.candidatesSubmittedToSAP,
            timeout: 10000,
            messageType: SNACKBAR_MESSAGE_TYPES.SUCCESS,
            marker: SNACKBAR_MARKER.MAINTENANCE,
            callback: () => {
              snackbarStore.removeMessage(submitToSAPSnackbarIdSuccess.value);
            }, // Reset the validationSnackbar to hide the message
          });
        } else {
          submitToSAPSnackbarIdSuccess.value = snackbarStore.showMessage({
            displayText: INSPECTION_MESSAGES.WORK_ITEMS_SENT_TO_SAP + "0",
            timeout: 10000,
            messageType: SNACKBAR_MESSAGE_TYPES.WARNING,
            marker: SNACKBAR_MARKER.MAINTENANCE,
            callback: () => {
              snackbarStore.removeMessage(submitToSAPSnackbarIdSuccess.value);
            }, // Reset the validationSnackbar to hide the message
          });
        }
      } else {
        submitToSAPSnackbarIdFailure.value.push(
          snackbarStore.showMessage({
            displayText: INSPECTION_MESSAGES.SUBMIT_TO_SAP_FAILED,
            timeout: 10000,
            messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
            marker: SNACKBAR_MARKER.MAINTENANCE,
            callback: () => {
              snackbarStore.removeMessage(submitToSAPSnackbarIdFailure.value[0]);
            }, // Reset the validationSnackbar to hide the message
          })
        );
      }
      inventoryStore.setIsSearching(false);
    };

    const clearSubmitToSAPSnackBars = () => {
      snackbarStore.removeMessage(submitToSAPSnackbarIdSuccess.value);
      for (const msg of submitToSAPSnackbarIdFailure.value || []) {
        snackbarStore.removeMessage(msg);
      }
    };

    const cancelChanges = async () => {
      //only called from Inspection Links
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        await readDataFromAPI();
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        await readData();
      }
    };

    const printD491 = () => {
      return { reportPage: "D491-Inv-Maintenance-Screen", marker: SNACKBAR_MARKER.MAINTENANCE };
    };

    onMounted(async () => {
      if (props.moduleType == MODULE_TYPES.MANAGEMENT) {
        await readDataFromAPI();
      } else if (props.moduleType == MODULE_TYPES.INSPECTION) {
        await readData();
      }
    });

    //watch for validationSnackbar
    watch(
      () => inventoryStore.showValidationSnackbar,
      (newVal) => {
        if (newVal) {
          snackBarId.value = snackbarStore.showMessage({
            displayText: INSPECTION_MESSAGES.VALIDATION_FAILED_MESSAGE,
            timeout: 10000,
            messageType: SNACKBAR_MESSAGE_TYPES.ERROR,
            marker: SNACKBAR_MARKER.MAINTENANCE,
            callback: () => {
              inventoryStore.showValidationSnackbar = false;
            }, // Reset the validationSnackbar to hide the message
          });
        } else {
          snackbarStore.removeMessage(snackBarId.value);
        }
      }
    );

    return {
      submitMaintenanceItemsToSAP,
      readData,
      resetItems,
      proposedMaintenanceTable,
      completedMaintenanceTable,
      printD491,
      completed,
      saveCandidate,
      proposed,
      isEditable,
      inspectionStore,
      showMessageEvent,
      hideMessageEvent,
      loadCandidates,
      toggleAllPanels,
      panel,
      readDataFromAPI,
      saveChanges,
      cancelChanges,
      maintenanceValue,
      structureHeader,
      changePriorityHistory,
      changeHistoryDialog,
      getPriorityHistory,
      selectedWcId,
      configStore,
      REFERENCE_TABLE,
      MODULE_TYPES,
      structureServTypOn,
    };
  },
  components: {
    MaintenanceTable,
    ExpandCollapseToggle,
    ValidationCountsHeader,
  },
});
</script>
